/**
 * Session and profile state.
 *
 * TWO STATE MACHINES, DELIBERATELY SEPARATE
 *
 *   authStatus    — does Supabase think you are signed in?
 *   profileStatus — does the Attest database know who you are?
 *
 * They are not the same question and the second can fail while the first
 * succeeds. public.users rows are not created automatically (Decision #35), so
 * a valid JWT with no profile row is a real, reachable state in which every
 * query returns zero rows and no error. Collapsing these into one "loading"
 * flag is exactly how that becomes "the app is broken" instead of "you are not
 * set up yet".
 */
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { setMonitoringUser } from '../lib/monitoring.js';
import { apiFetch } from '../lib/api.js';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [authStatus, setAuthStatus] = useState('loading'); // loading | signed-out | signed-in
  const [session, setSession] = useState(null);

  const [profileStatus, setProfileStatus] = useState('idle'); // idle | loading | ok | not-provisioned | inactive | error
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState(null);

  // Which user id we have already fetched a profile for. Guards against the
  // refetch storm: onAuthStateChange fires TOKEN_REFRESHED roughly hourly, and
  // the profile has not changed just because the token has.
  const fetchedForUserId = useRef(null);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data?.session ?? null);
      setAuthStatus(data?.session ? 'signed-in' : 'signed-out');
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setAuthStatus(nextSession ? 'signed-in' : 'signed-out');
    });

    return () => {
      active = false;
      subscription?.subscription?.unsubscribe();
    };
  }, []);

  const loadProfile = useCallback(async () => {
    setProfileStatus('loading');
    setProfileError(null);
    try {
      const data = await apiFetch('/api/v1/me');
      setProfile(data);
      if (!data.profileProvisioned) setProfileStatus('not-provisioned');
      else if (!data.isActive) setProfileStatus('inactive');
      else setProfileStatus('ok');
    } catch (err) {
      // A 401 already signed us out; anything else is a server or network
      // problem and must NOT be mistaken for "not provisioned".
      setProfile(null);
      setProfileError(err);
      setProfileStatus(err?.status === 401 ? 'idle' : 'error');
    }
  }, []);

  useEffect(() => {
    if (authStatus === 'signed-out') {
      fetchedForUserId.current = null;
      setProfile(null);
      setProfileStatus('idle');
      setProfileError(null);
      return;
    }

    const userId = session?.user?.id;
    setMonitoringUser(authStatus === 'signed-in' ? userId ?? null : null);
    if (authStatus !== 'signed-in' || !userId) return;
    if (fetchedForUserId.current === userId) return; // token refresh, not a new user

    fetchedForUserId.current = userId;
    loadProfile();
  }, [authStatus, session?.user?.id, loadProfile]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const retryProfile = useCallback(() => {
    fetchedForUserId.current = session?.user?.id ?? null;
    return loadProfile();
  }, [loadProfile, session?.user?.id]);

  const value = {
    authStatus,
    session,
    user: session?.user ?? null,
    profileStatus,
    profile,
    profileError,
    signOut,
    retryProfile,
    /** Permission check for menu gating. The database still decides. */
    can: (permission) => Boolean(profile?.permissions?.includes(permission)),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
